import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useLenis } from "lenis/react";
import { ArrowDown } from "lucide-react";
import { IMAGES } from "@/config";

const LINES = ["WE BUILD", "GROWTH", "SYSTEMS."];

export default function Hero({ delayBase = 0 }) {
  const ref = useRef(null);
  const navigate = useNavigate();
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative flex min-h-screen items-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src={IMAGES.hero}
          alt="Strategic command interior — The Polemikoi Agency"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-black/60" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-[1600px] px-6 pb-24 pt-40 md:px-12">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: delayBase + 1.1 }}
          className="mb-8 flex items-center gap-3 font-mono text-xs tracking-[0.3em] uppercase text-zinc-400"
        >
          <span className="h-1.5 w-1.5 animate-pulse bg-crimson" />
          Creative + Advertising + Technology + Automation
        </motion.p>

        <h1 className="font-display font-black uppercase leading-[0.95] tracking-tighter text-white">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: delayBase + 0.25 + i * 0.18, ease: [0.16, 1, 0.3, 1] }}
                className="block text-[13vw] sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: delayBase + 1.2 }}
          className="mt-8 max-w-xl text-base leading-relaxed text-zinc-300 md:text-lg"
        >
          The Polemikoi Agency combines strategy, creative, advertising, web development,
          and AI automation to help businesses attract more customers and convert more
          opportunities into revenue.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: delayBase + 1.4 }}
          className="relative mt-12 flex flex-wrap items-center gap-5"
        >
          <div className="absolute -inset-8 -z-10 bg-crimson/20 blur-[100px]" aria-hidden="true" />
          <button
            data-testid="hero-book-call-btn"
            onClick={() => navigate("/book")}
            className="group bg-white px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-black transition-colors duration-300 hover:bg-crimson hover:text-white"
          >
            Book a Strategy Call
          </button>
          <button
            data-testid="hero-explore-services-btn"
            onClick={() => lenis?.scrollTo("#services", { offset: -72 })}
            className="border border-white/30 px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:border-white hover:bg-white hover:text-black"
          >
            Explore Our Services
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: delayBase + 2, duration: 1 }}
        className="absolute bottom-8 right-8 z-10 hidden items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 md:flex"
      >
        Scroll <ArrowDown size={14} className="animate-bounce" />
      </motion.div>
    </section>
  );
}
